/**
 * V3 numeric ids ↔ V4 uuids for scenario triggers and actions. Shared by
 * `scenarios.js` and `actionConfigs.js` so the two can't drift on how a
 * sentinel, an already-V4 uuid or an unresolvable id is treated.
 */

import { forAnyObject, forConcerningObject } from '../../utils/models/Scenario';

export const toIdArray = rawIds =>
    Array.isArray(rawIds) ? rawIds : rawIds?.toJS?.() || rawIds?.toArray?.() || [];

// V3 spells "any" and "the concerning device" as negative ids inside the id
// array; V4 spells them as booleans on the filter/recipients.
export const isAnyId = id => Number(id) === forAnyObject;
export const isConcerningId = id => Number(id) === forConcerningObject;

// V4-native sessions hold uuids everywhere, so anything long and non-numeric
// passes through untouched.
export const isV4Uuid = raw =>
    typeof raw === 'string' && raw.length > 20 && /[^0-9]/.test(raw);

/**
 * V3 ids → V4 uuids. Sentinels are skipped (the caller turns them into flags);
 * an id with no known uuid is dropped, since V4 rejects a non-uuid outright and
 * a wrong uuid would silently address the wrong entity.
 */
export function translateIds(rawIds, map) {
    const arr = toIdArray(rawIds);
    if (!arr.length) return [];
    const out = [];
    for (const raw of arr) {
        if (raw == null || raw === '') continue;
        if (typeof raw === 'number' && raw < 0) continue;
        if (isV4Uuid(raw)) {
            out.push(raw);
            continue;
        }
        const numeric = +raw;
        const uuid = map?.[numeric] ?? map?.[String(numeric)];
        if (uuid) out.push(uuid);
    }
    return out;
}

/** V4 uuids → the ids the V3 pickers hold. Unmapped uuids pass through. */
export function untranslateIds(rawIds, map) {
    return toIdArray(rawIds)
        .filter(id => id != null && id !== '')
        .map(id => map?.[id] ?? id);
}

/** The V3 numeric ids in `rawIds` — what the saga resolves before a save. */
export function referencedV3Ids(rawIds) {
    const out = [];
    for (const raw of toIdArray(rawIds)) {
        if (raw == null || isAnyId(raw) || isConcerningId(raw) || isV4Uuid(raw)) continue;
        const numeric = +raw;
        if (!Number.isFinite(numeric) || numeric <= 0) continue;
        out.push(numeric);
    }
    return out;
}
